import { createElement, useEffect, useRef } from 'react';
import './InlineEdit.scss';

interface InlineTextProps {
  value: string;
  onChange: (next: string) => void;
  renderTag?: string;
  renderClassName?: string;
  placeholder?: string;
  ariaLabel?: string;
}

const InlineText = ({
  value,
  onChange,
  renderTag = 'span',
  renderClassName,
  placeholder,
  ariaLabel,
}: InlineTextProps) => {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (ref.current && ref.current.innerText !== value) {
      ref.current.innerText = value;
    }
  }, [value]);

  const handleInput = () => {
    if (!ref.current) return;
    const next = ref.current.innerText.replace(/\n/g, '');
    if (next !== value) onChange(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain').replace(/\r?\n/g, ' ');
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();
    range.insertNode(document.createTextNode(text));
    range.collapse(false);
    handleInput();
  };

  if (typeof window === 'undefined') {
    return createElement(renderTag, { className: renderClassName }, value);
  }

  const className = ['inline-text', renderClassName].filter(Boolean).join(' ');

  return createElement(renderTag, {
    ref,
    className,
    contentEditable: true,
    suppressContentEditableWarning: true,
    spellCheck: true,
    'data-placeholder': placeholder,
    'aria-label': ariaLabel,
    onInput: handleInput,
    onKeyDown: handleKeyDown,
    onPaste: handlePaste,
  });
};

export default InlineText;
